import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import PublicScheduleLayout from '../PublicSchedule/components/PublicScheduleLayout';
import PublicScheduleHeader from '../PublicSchedule/components/PublicScheduleHeader';
import TrustFooter from '../PublicSchedule/components/TrustFooter';

import {
    GetPublicAppointmentConfirmationAPI,
    RespondPublicAppointmentConfirmationAPI,
    GetRescheduleAvailableSlotsAPI,
    ReschedulePublicAppointmentAPI,
} from '../../api/PublicAppointmentConfirmationAPI';

import { GetPublicAvailableSlotsRangeAPI } from '../../api/PublicScheduleAPI';

import StepDate from '../PublicSchedule/steps/StepDate';
import StepTime from '../PublicSchedule/steps/StepTime';

import { normalizePhone } from '../../utils/phoneUtils';

import '../PublicSchedule/PublicSchedulePage.css';
import './PublicAppointmentConfirmationPage.css';

/**
 * PublicAppointmentConfirmationPage
 *
 * Public (no-auth) page that lets a patient confirm, cancel or request
 * reschedule of a specific appointment via a secure token URL.
 *
 * Route: /confirmar/:companySlug/:token
 */

// ─── Icons (inline SVGs) ─────────────────────────────────────────────────────

const IconCalendar = (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
);
const IconClock = (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
    </svg>
);
const IconStethoscope = (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
        <path d="M6 3v6a4 4 0 0 0 8 0V3" />
        <path d="M10 13v2a4 4 0 0 0 8 0" />
        <circle cx="18" cy="17" r="2" />
    </svg>
);
const IconUser = (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
    </svg>
);
const IconCalendarCheck = (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
        <polyline points="9 15 11 17 15 13" />
    </svg>
);
const IconCheckCircle = (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
        <circle cx="12" cy="12" r="10" />
        <polyline points="8 12 11 15 16 9" />
    </svg>
);
const IconRefresh = (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
        <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
        <polyline points="21 3 21 8 16 8" />
        <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
        <polyline points="3 21 3 16 8 16" />
    </svg>
);
const IconXCircle = (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
        <circle cx="12" cy="12" r="10" />
        <line x1="15" y1="9" x2="9" y2="15" />
        <line x1="9" y1="9" x2="15" y2="15" />
    </svg>
);
const IconLock = (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
);
const IconArrowLeft = (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
        <line x1="19" y1="12" x2="5" y2="12" />
        <polyline points="12 19 5 12 12 5" />
    </svg>
);
const IconInfo = (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="16" x2="12" y2="12" />
        <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
);
const IconWhatsapp = (props) => (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.077 4.487.71.306 1.262.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.29.173-1.414-.074-.124-.272-.198-.57-.347z"/>
        <path d="M20.52 3.449C18.24 1.245 15.24 0 12.05 0 5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.304-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0 0 20.52 3.449M12.05 21.785h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.002-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884"/>
    </svg>
);


// ─── Date/time helpers ──────────────────────────────────────────────────────

const WEEKDAYS_LONG = [
    'domingo', 'segunda-feira', 'terça-feira', 'quarta-feira',
    'quinta-feira', 'sexta-feira', 'sábado',
];
const WEEKDAYS_SHORT = [
    'Domingo', 'Segunda', 'Terça', 'Quarta',
    'Quinta', 'Sexta', 'Sábado',
];
const MONTHS = [
    'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
    'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
];

function parseIsoDate(isoDate) {
    if (!isoDate || typeof isoDate !== 'string') return null;
    const [y, m, d] = isoDate.split('-').map((n) => parseInt(n, 10));
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
}

/** "Quinta, 02 de julho" */
function formatShortDate(isoDate) {
    const dt = parseIsoDate(isoDate);
    if (!dt) return isoDate || '';
    const dd = String(dt.getDate()).padStart(2, '0');
    return `${WEEKDAYS_SHORT[dt.getDay()]}, ${dd} de ${MONTHS[dt.getMonth()]}`;
}
/** "quarta-feira, 15 de janeiro de 2026" */
function formatFullDate(isoDate) {
    const dt = parseIsoDate(isoDate);
    if (!dt) return isoDate || '';
    return `${WEEKDAYS_LONG[dt.getDay()]}, ${dt.getDate()} de ${MONTHS[dt.getMonth()]} de ${dt.getFullYear()}`;
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function PublicAppointmentConfirmationPage() {
    const { token } = useParams();

    const [loading, setLoading]         = useState(true);
    const [initialStatus, setInitialStatus] = useState(null);
    const [appointment, setAppointment] = useState(null);
    const [initialAction, setInitialAction] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');

    const [submitting, setSubmitting]   = useState(false);
    const [pendingAction, setPendingAction] = useState(null); // 'confirm' | 'cancel' | 'reschedule'
    const [doneAction, setDoneAction]   = useState(null);
    const [postSubmitError, setPostSubmitError] = useState('');

    // V3 — Reschedule slot picker state
    const [rescheduleStep, setRescheduleStep] = useState(false); // true = showing slot picker
    const [selectedSlot, setSelectedSlot]     = useState(null);  // { date, time } | null

    // Tracks if patient previously confirmed (so we can show badge + disable button)
    const [previouslyConfirmed, setPreviouslyConfirmed] = useState(false);

    // ─── Deep link support (Phase 2 — WhatsApp buttons) ─────────────────────
    // The WhatsApp message now carries two native Z-API URL buttons:
    //   • "Confirmar presença"       → …?action=confirm
    //   • "Reagendar ou cancelar"    → …?action=choose
    // "Confirmar presença" grava a confirmação ao abrir (ver confirmNow).
    // Additional deep-link actions supported in case a link is crafted
    // directly (email, other channel, future features):
    //   • ?action=cancel     → jumps straight to "Você tem certeza?" (cancel)
    //   • ?action=reschedule → jumps straight to "Você tem certeza?" (reschedule)
    // If action=choose (or omitted / invalid), the page renders the normal
    // 3-CTA screen. The deep link is only honored when status === 'active';
    // other statuses (used/expired/invalid/not_actionable) always win.
    const deepLinkAction = useMemo(() => {
        if (typeof window === 'undefined') return null;
        try {
            const params = new URLSearchParams(window.location.search);
            const raw = (params.get('action') || '').toLowerCase().trim();
            if (raw === 'confirm' || raw === 'cancel' || raw === 'reschedule') {
                return raw;
            }
            return null; // 'choose' or anything else → normal 3-CTA screen
        } catch (_e) {
            return null;
        }
    }, []);

    // onSettled(res) roda antes de aplicar a resposta (usado pela confirmação direta do link)
    const submitAction = useCallback((action, onSettled) => {
        setSubmitting(true);
        setPostSubmitError('');

        RespondPublicAppointmentConfirmationAPI(token, action, (res) => {
            setSubmitting(false);
            setPendingAction(null);
            if (onSettled) onSettled(res);
            if (!res || res.success === false) {
                // Se status='used' mas a ação foi 'confirm', trata como sucesso
                // (confirmar é idempotente — pode re-confirmar sem problema)
                if (res && res.status === 'used' && action === 'confirm') {
                    setDoneAction(action);
                    return;
                }
                if (res && (res.status === 'used' || res.status === 'expired' || res.status === 'invalid' || res.status === 'not_actionable')) {
                    setInitialStatus(res.status);
                    setErrorMessage(res.error || '');
                    // Mostra o que já ficou registrado, nunca o card genérico
                    setInitialAction(res.action || null);
                    return;
                }
                setPostSubmitError((res && res.error) || 'Não foi possível registrar sua resposta. Tente novamente.');
                return;
            }
            setDoneAction(action);
        });
    }, [token]);

    // Confirmar é um toque só, no link da mensagem e no botão da página: sem o
    // "Você tem certeza?" (quem fechava a página nessa etapa não confirmava).
    // Cancelar e reagendar continuam com a pergunta. Falha de rede abre a
    // pergunta, que mostra o erro e o "tentar de novo".
    const confirmNow = useCallback((onSettled) => {
        submitAction('confirm', (submitted) => {
            if (onSettled) onSettled();
            const handled = submitted && (submitted.success !== false
                || ['used', 'expired', 'invalid', 'not_actionable'].includes(submitted.status));
            if (!handled) setPendingAction('confirm');
        });
    }, [submitAction]);

    useEffect(() => {
        let cancelled = false; // Prevents stale callback from overwriting state (React 18 StrictMode)

        if (!token) {
            setInitialStatus('invalid');
            setErrorMessage('Link de confirmação inválido.');
            setLoading(false);
            return;
        }

        GetPublicAppointmentConfirmationAPI(token, (res) => {
            if (cancelled) return; // Stale callback — ignore

            setLoading(false);
            if (!res || res.success === false) {
                // Se status='used' mas a ação anterior foi 'confirm',
                // permite voltar atrás e escolher outra resposta
                if (res && res.status === 'used' && res.action === 'confirm') {
                    setInitialStatus('active');
                    setAppointment(res.appointment || null);
                    setInitialAction(null);
                    setPreviouslyConfirmed(true);
                    return;
                }

                setInitialStatus(res && res.status ? res.status : 'invalid');
                setErrorMessage((res && res.error) || 'Link de confirmação inválido.');
                setAppointment(res && res.appointment ? res.appointment : null);
                return;
            }
            const status = res.status || 'active';
            // Se já foi usado com 'confirm', permite re-escolher
            const effectiveStatus = (status === 'used' && res.action === 'confirm')
                ? 'active'
                : status;
            if (status === 'used' && res.action === 'confirm') {
                setPreviouslyConfirmed(true);
            }
            setInitialStatus(effectiveStatus);
            setAppointment(res.appointment || null);
            // Use synthetic 'reschedule_completed' when backend confirms a direct reschedule was done
            const effectiveAction = (res.action === 'reschedule' && res.reschedule_completed)
                ? 'reschedule_completed'
                : (res.action || null);
            setInitialAction(effectiveStatus === 'active' ? null : effectiveAction);

            // Deep-link pre-selection
            // Note: for reschedule deep-link, we only go to slot picker if resource allows public schedule.
            // Since appointment data is available here, we check resource_allows_public_schedule directly.
            // Já confirmado: o link "Confirmar presença" só mostra o selo, sem perguntar de novo.
            const alreadyConfirmed = status === 'used' && res.action === 'confirm';
            if (effectiveStatus === 'active' && deepLinkAction === 'confirm' && !alreadyConfirmed) {
                // O botão "Confirmar presença" da mensagem já é a resposta: grava na hora
                setLoading(true);
                confirmNow(() => setLoading(false));
            } else if (effectiveStatus === 'active' && deepLinkAction && !(alreadyConfirmed && deepLinkAction === 'confirm')) {
                setPendingAction(deepLinkAction);
                if (deepLinkAction === 'reschedule' && !!(res.appointment?.resource_allows_public_schedule)) {
                    // V4: só vai pro calendário se o resource permite agenda pública
                    setRescheduleStep(true);
                }
                // Se reschedule mas não permite agenda pública, fica no ConfirmActionStep
            }
        });

        return () => { cancelled = true; }; // Cleanup: cancel stale callback
    }, [token, deepLinkAction, confirmNow]);

    // ── Derived state ────────────────────────────────────────────────────────
    const clinic = appointment ? (appointment.clinic || null) : null;

    // V4 — whether the resource allows public scheduling (controls reschedule flow)
    // When true: show calendar slot picker. When false: show WhatsApp text flow.
    const resourceAllowsPublicSchedule = !!appointment?.resource_allows_public_schedule;

    const companyForHeader = clinic
        ? {
            id:      clinic.id,
            name:    clinic.name    || appointment?.clinic_name || '',
            logo:    clinic.logo    || '',
            city:    clinic.city    || '',
            state:   clinic.state   || '',
            phone:   clinic.phone   || '',
            address:    clinic.address    || '',
            number:     clinic.number     || '',
            complement: clinic.complement || '',
            district:   clinic.district   || '',
            cep:        clinic.cep        || '',
        }
        : (appointment ? { name: appointment.clinic_name || '' } : null);

    const shortDate = appointment ? formatShortDate(appointment.date) : '';
    const fullDate  = appointment ? formatFullDate(appointment.date)  : '';
    const time      = appointment ? (appointment.time || '') : '';

    // ── Render ───────────────────────────────────────────────────────────────
    return (
        <PublicScheduleLayout>
            <PublicScheduleHeader company={companyForHeader} />

            <main className="pac-main">
                {loading && (
                    <div className="pac-state pac-state--loading">
                        <div className="pac-spinner" aria-hidden="true" />
                        <p className="pac-state__text">Carregando informações do agendamento…</p>
                    </div>
                )}

                {!loading && initialStatus === 'invalid' && (
                    <StateCard variant="error" title="Link inválido"
                        text={errorMessage || 'Este link de confirmação não é válido. Verifique se você copiou o endereço completo.'}
                        clinic={clinic} />
                )}

                {!loading && initialStatus === 'expired' && (
                    <StateCard variant="warn" title="Tempo para confirmação encerrado"
                        text="O prazo para confirmar, cancelar ou reagendar por aqui já passou. Entre em contato diretamente pelo WhatsApp."
                        clinic={clinic}>
                        {appointment && (
                            <AppointmentGrid appointment={appointment} shortDate={shortDate} fullDate={fullDate} time={time} />
                        )}
                    </StateCard>
                )}

                {!loading && initialStatus === 'not_actionable' && !doneAction && (
                    <StateCard variant="warn" title="Ações indisponíveis"
                        text={errorMessage || 'Este agendamento não pode mais ser atualizado por aqui. Se precisar, entre em contato pelo WhatsApp.'}
                        clinic={clinic} />
                )}

                {!loading && initialStatus === 'used' && !doneAction && (
                    <StateCard
                        variant={buildUsedVariant(initialAction)}
                        title={buildUsedTitle(initialAction)}
                        text={buildUsedText(initialAction)}
                        clinic={clinic}
                        pill={buildSuccessPill(initialAction)}
                    >
                        {appointment && (
                            <AppointmentGrid appointment={appointment} shortDate={shortDate} fullDate={fullDate} time={time} />
                        )}
                    </StateCard>
                )}

                {!loading && initialStatus === 'active' && !doneAction && !pendingAction && (
                    <>
                        <div className="pac-intro">
                            <div className="pac-intro__badge" aria-hidden="true">
                                <IconCalendarCheck width="22" height="22" />
                            </div>
                            <div className="pac-intro__text-wrap">
                                <h1 className="pac-intro__title">
                                    Olá{appointment && (appointment.patient_first_name || appointment.patient_name) ? `, ${appointment.patient_first_name || (appointment.patient_name || '').split(' ')[0]}` : ''}!
                                </h1>
                                <p className="pac-intro__text">
                                    {previouslyConfirmed
                                        ? 'Você já confirmou sua presença. Precisa alterar algo?'
                                        : 'Confirme se você poderá comparecer ao seu agendamento.'}
                                </p>
                            </div>
                        </div>

                        {/* Badge de confirmado sobre os dados do agendamento */}
                        {previouslyConfirmed && (
                            <div className="pac-confirmed-badge">
                                <IconCheckCircle width="16" height="16" />
                                <span>Presença confirmada</span>
                            </div>
                        )}

                        <AppointmentGrid appointment={appointment} shortDate={shortDate} fullDate={fullDate} time={time} />

                        <div className="pac-cta-stack">
                            {previouslyConfirmed ? (
                                <CTAButton
                                    variant="primary-muted"
                                    icon={<IconCheckCircle width="24" height="24" />}
                                    title="Presença já confirmada"
                                    subtitle="Você já confirmou que irá comparecer"
                                    onClick={() => {}}
                                    disabled={true}
                                />
                            ) : (
                                <CTAButton
                                    variant="primary"
                                    icon={<IconCheckCircle width="24" height="24" />}
                                    title="Confirmar presença"
                                    subtitle="Eu irei comparecer normalmente"
                                    onClick={() => confirmNow()}
                                    disabled={submitting}
                                />
                            )}
                            <CTAButton
                                variant="secondary"
                                icon={<IconRefresh width="22" height="22" />}
                                title="Solicitar reagendamento"
                                subtitle="Preciso de outro horário"
                                onClick={() => {
                                    setPendingAction('reschedule');
                                    // V4: só vai direto pro calendário se o resource permite agenda pública
                                    if (resourceAllowsPublicSchedule) {
                                        setRescheduleStep(true);
                                    }
                                    // Se não permite, vai para o ConfirmActionStep (tela "Tem certeza?")
                                }}
                                disabled={submitting}
                            />
                            <CTAButton
                                variant="danger-outline"
                                icon={<IconXCircle width="24" height="24" />}
                                title="Preciso cancelar"
                                subtitle="Não poderei comparecer"
                                onClick={() => setPendingAction('cancel')}
                                disabled={submitting}
                            />
                        </div>

                        {clinic && (
                            <div className="pac-active-contact">
                                <p className="pac-active-contact__label">Prefere falar por WhatsApp?</p>
                                <ContactClinicButton clinic={clinic} />
                            </div>
                        )}

                        <p className="pac-security-note">
                            <IconLock width="12" height="12" />
                            Suas informações estão seguras e esta ação será registrada automaticamente.
                        </p>
                    </>
                )}

                {!loading && initialStatus === 'active' && !doneAction && pendingAction && !rescheduleStep && (
                    <ConfirmActionStep
                        action={pendingAction}
                        appointment={appointment}
                        clinic={clinic}
                        shortDate={shortDate}
                        fullDate={fullDate}
                        time={time}
                        submitting={submitting}
                        onBack={() => setPendingAction(null)}
                        onConfirm={() => {
                            if (pendingAction === 'reschedule') {
                                if (resourceAllowsPublicSchedule) {
                                    // V3/V4: resource permite agenda pública → vai pro calendário
                                    setRescheduleStep(true);
                                } else {
                                    // V4: resource NÃO permite agenda pública → registra direto (clínica já recebe notificação automática)
                                    // openWhatsappForAction('reschedule', appointment, clinic, shortDate, time);
                                    submitAction('reschedule');
                                }
                                return;
                            }
                            // Cancel: clínica já recebe notificação automática via backend
                            // if (pendingAction === 'cancel') {
                            //     openWhatsappForAction(pendingAction, appointment, clinic, shortDate, time);
                            // }
                            submitAction(pendingAction);
                        }}
                        postSubmitError={postSubmitError}
                    />
                )}

                {/* V3 — Reschedule slot picker step */}
                {!loading && initialStatus === 'active' && !doneAction && pendingAction === 'reschedule' && rescheduleStep && (
                    <RescheduleSlotPicker
                        token={token}
                        appointment={appointment}
                        clinic={clinic}
                        shortDate={shortDate}
                        time={time}
                        selectedSlot={selectedSlot}
                        onSelectSlot={setSelectedSlot}
                        submitting={submitting}
                        setSubmitting={setSubmitting}
                        onBack={() => {
                            setRescheduleStep(false);
                            setPendingAction(null);
                            setSelectedSlot(null);
                        }}
                        onConfirmSlot={(slot) => {
                            // Open WhatsApp with selected slot in the message
                            openWhatsappRescheduleWithSlot(appointment, clinic, shortDate, time, slot);
                            submitAction('reschedule');
                        }}
                        onRescheduleSlot={(slot) => {
                            // V5: Direct reschedule — creates new appointment via backend
                            setSubmitting(true);
                            setPostSubmitError('');
                            ReschedulePublicAppointmentAPI(token, slot.date, slot.time, (res) => {
                                setSubmitting(false);
                                if (!res || res.success === false) {
                                    if (res && (res.status === 'used' || res.status === 'expired' || res.status === 'invalid' || res.status === 'not_actionable')) {
                                        setInitialStatus(res.status);
                                        setErrorMessage(res.error || '');
                                        setPendingAction(null);
                                        setRescheduleStep(false);
                                        return;
                                    }
                                    setPostSubmitError((res && res.error) || 'Não foi possível reagendar. Tente novamente.');
                                    return;
                                }
                                // Update appointment data to reflect new date/time in SuccessScreen
                                setAppointment(prev => ({
                                    ...prev,
                                    date: slot.date,
                                    time: slot.time,
                                }));
                                setPendingAction(null);
                                setDoneAction('reschedule_done');
                            });
                        }}
                        onSkipSlot={() => {
                            // Fallback: open WhatsApp without slot
                            openWhatsappForAction('reschedule', appointment, clinic, shortDate, time);
                            submitAction('reschedule');
                        }}
                        postSubmitError={postSubmitError}
                    />
                )}

                {!loading && doneAction && (
                    <SuccessScreen
                        action={doneAction}
                        appointment={appointment}
                        shortDate={shortDate}
                        fullDate={fullDate}
                        time={time}
                        clinic={clinic}
                        selectedSlot={selectedSlot}
                        onReopenWhatsapp={() => {
                            if (doneAction === 'reschedule' && selectedSlot) {
                                openWhatsappRescheduleWithSlot(appointment, clinic, shortDate, time, selectedSlot);
                            } else {
                                openWhatsappForAction(doneAction, appointment, clinic, shortDate, time);
                            }
                        }}
                    />
                )}

                {!loading && initialStatus === 'error' && (
                    <StateCard variant="error" title="Não foi possível carregar"
                        text={errorMessage || 'Erro ao carregar o agendamento. Tente novamente em alguns instantes.'}
                        clinic={clinic} />
                )}
            </main>

            <TrustFooter />
        </PublicScheduleLayout>
    );
}

// ─── Sub-components ──────────────────────────────────────────────────────────

/**
 * AppointmentGrid — grade de cards com Data / Horário / Procedimento / Profissional.
 * Data e Horário ficam lado a lado; Procedimento e Profissional em linha cheia.
 */
function AppointmentGrid({ appointment, shortDate, fullDate, time }) {
    if (!appointment) return null;

    const professional = appointment.professional_name || '';
    const procedure    = appointment.procedure_name    || '';
    const patientName  = appointment.patient_first_name || (appointment.patient_name || '').split(' ')[0] || '';

    return (
        <section className="pac-card">
            {/* Paciente */}
            {patientName && (
                <div className="pac-card__row">
                    <InfoItem
                        icon={<IconUser width="26" height="26" />}
                        iconTone="muted"
                        label="Paciente"
                        value={patientName}
                    />
                </div>
            )}

            {/* Linha 1: Data + Horário (2 colunas, ícones rosa) */}
            <div className="pac-card__row pac-card__row--split">
                <InfoItem
                    icon={<IconCalendar width="26" height="26" />}
                    iconTone="pink"
                    label="Data"
                    value={shortDate}
                    title={fullDate}
                />
                <InfoItem
                    icon={<IconClock width="26" height="26" />}
                    iconTone="pink"
                    label="Horário"
                    value={time}
                />
            </div>

            {procedure && (
                <div className="pac-card__row">
                    <InfoItem
                        icon={<IconStethoscope width="26" height="26" />}
                        iconTone="muted"
                        label="Procedimento"
                        value={procedure}
                    />
                </div>
            )}

            {professional && (
                <div className="pac-card__row">
                    <InfoItem
                        icon={<IconUser width="26" height="26" />}
                        iconTone="muted"
                        label="Profissional"
                        value={professional}
                    />
                </div>
            )}
        </section>
    );
}

function InfoItem({ icon, iconTone = 'pink', label, value, title }) {
    return (
        <div className="pac-item" title={title || undefined}>
            <span className={`pac-item__icon pac-item__icon--${iconTone}`} aria-hidden="true">
                {icon}
            </span>
            <div className="pac-item__body">
                <div className="pac-item__label">{label}</div>
                <div className="pac-item__value">{value || '—'}</div>
            </div>
        </div>
    );
}

function CTAButton({ variant = 'primary', icon, title, subtitle, onClick, disabled }) {
    return (
        <button
            type="button"
            className={`pac-cta pac-cta--${variant}`}
            onClick={onClick}
            disabled={disabled}
        >
            <span className="pac-cta__icon" aria-hidden="true">{icon}</span>
            <span className="pac-cta__text">
                <span className="pac-cta__title">{title}</span>
                {subtitle && <span className="pac-cta__subtitle">{subtitle}</span>}
            </span>
        </button>
    );
}

/**
 * SuccessScreen — tela compacta de confirmação/cancelamento/reagendamento
 * exibida após a ação do paciente ser registrada com sucesso.
 *
 * Layout inspirado no mockup:
 *   ○ Ícone verde (grande) de check
 *   ● Título "Tudo certo!"
 *   ● Subtítulo curto
 *   ● Sub-card interno com pill de status + Data / Horário / Profissional
 *   ● Rodapé cinza-clarinho: "Se precisar alterar sua consulta,
 *     entre em contato pelo WhatsApp."  (abre WhatsApp)
 */
function SuccessScreen({ action, appointment, shortDate, fullDate, time, clinic, onReopenWhatsapp }) {
    const pill = buildSuccessPill(action);       // {label, tone}
    const patientFirstName = appointment?.patient_first_name || (appointment?.patient_name || '').split(' ')[0] || '';
    const title = patientFirstName ? `Tudo certo, ${patientFirstName}!` : 'Tudo certo!';
    const subtitle = action === 'reschedule_done'
        ? 'Seu agendamento foi reagendado com sucesso.'
        : 'Sua resposta foi registrada com sucesso.';
    const showReopenWhatsapp =
        (action === 'cancel' || action === 'reschedule') &&
        !!buildActionWhatsappUrl(action, appointment, clinic, shortDate, time);

    return (
        <div className="pac-success">
            <div className="pac-success__icon" aria-hidden="true">
                <IconCheckCircle width="34" height="34" />
            </div>

            <h2 className="pac-success__title">{title}</h2>
            <p className="pac-success__subtitle">{subtitle}</p>

            <div className="pac-success__pill-wrap">
                <div className="pac-success__summary-label">Sua resposta</div>
                <span className={`pac-pill pac-pill--${pill.tone}`}>
                    {pill.icon}
                    {pill.label}
                </span>
            </div>

            {appointment && (
                <AppointmentGrid
                    appointment={appointment}
                    shortDate={shortDate}
                    fullDate={fullDate}
                    time={time}
                />
            )}

            {showReopenWhatsapp ? (
                <>
                    <p className="pac-success__hint">
                        <IconInfo width="14" height="14" />
                        Não abriu o WhatsApp? Toque aqui para enviar sua mensagem:
                    </p>
                    <button
                        type="button"
                        className="pac-whatsapp-btn"
                        onClick={onReopenWhatsapp}
                    >
                        <span className="pac-whatsapp-btn__icon" aria-hidden="true">
                            <IconWhatsapp width="20" height="20" />
                        </span>
                        <span>Abrir WhatsApp novamente</span>
                    </button>
                </>
            ) : (
                <>
                    <p className="pac-success__hint">
                        <IconInfo width="14" height="14" />
                        Se precisar alterar algo no seu agendamento, fale conosco:
                    </p>
                    <ContactClinicButton clinic={clinic} />
                </>
            )}
        </div>
    );
}

/**
 * ConfirmActionStep — passo intermediário "Você tem certeza…?" para as 3 ações.
 * Renderizado no lugar da tela `active` quando pendingAction != null.
 *
 * Layout do mockup:
 *   ← Voltar (topo)
 *   ◯ Ícone circular grande colorido conforme a ação
 *   ● Título/pergunta grande
 *   ● Subtítulo com contexto (cancel/reschedule avisam sobre liberar horário)
 *   ● Sub-card com Data | Horário / Procedimento / Profissional
 *   ● Botão primário "Sim, {ação}"
 *   ● Botão outline "Voltar"
 */
function ConfirmActionStep({
    action, appointment, shortDate, fullDate, time,
    submitting, onBack, onConfirm, postSubmitError,
}) {
    const cfg = getConfirmStepConfig(action);
    const patientFirstName = appointment?.patient_first_name || (appointment?.patient_name || '').split(' ')[0] || '';

    return (
        <div className={`pac-confirm pac-confirm--${cfg.tone}`}>
            <button
                type="button"
                className="pac-confirm__back"
                onClick={onBack}
                disabled={submitting}
                aria-label="Voltar"
            >
                <IconArrowLeft width="20" height="20" />
            </button>

            <div className={`pac-confirm__icon pac-confirm__icon--${cfg.tone}`} aria-hidden="true">
                {cfg.icon}
            </div>

            <h2 className="pac-confirm__title">
                {patientFirstName ? `${patientFirstName}, ${cfg.title.charAt(0).toLowerCase()}${cfg.title.slice(1)}` : cfg.title}
            </h2>
            <p className="pac-confirm__text">{cfg.description}</p>

            {appointment && (
                <AppointmentGrid
                    appointment={appointment}
                    shortDate={shortDate}
                    fullDate={fullDate}
                    time={time}
                />
            )}

            {postSubmitError && (
                <p className="pac-error-inline">{postSubmitError}</p>
            )}

            <div className="pac-confirm__actions">
                <button
                    type="button"
                    className={`pac-btn pac-btn--solid pac-btn--${cfg.tone}`}
                    onClick={onConfirm}
                    disabled={submitting}
                >
                    {submitting ? 'Enviando…' : cfg.confirmLabel}
                </button>
                <button
                    type="button"
                    className="pac-btn pac-btn--ghost"
                    onClick={onBack}
                    disabled={submitting}
                >
                    Voltar
                </button>
            </div>
        </div>
    );
}

function getConfirmStepConfig(action) {
    switch (action) {
        case 'confirm':
            return {
                tone: 'confirm',
                icon: <IconCheckCircle width="40" height="40" />,
                title: 'Você tem certeza que deseja confirmar sua presença?',
                description: 'Avisaremos que você irá comparecer normalmente no horário marcado.',
                confirmLabel: 'Sim, confirmar presença',
            };
        case 'cancel':
            return {
                tone: 'cancel',
                icon: <IconXCircle width="40" height="40" />,
                title: 'Você tem certeza que deseja cancelar seu agendamento?',
                description: 'Ao confirmar, abriremos o WhatsApp para você nos enviar uma mensagem avisando do cancelamento. Voltar atrás depois ficará sujeito à disponibilidade.',
                confirmLabel: 'Sim, cancelar agendamento',
            };
        case 'reschedule':
            return {
                tone: 'reschedule',
                icon: <IconRefresh width="40" height="40" />,
                title: 'Deseja solicitar um reagendamento?',
                description: 'Ao confirmar, abriremos o WhatsApp para você nos avisar. Em seguida combinamos um novo horário conforme a disponibilidade.',
                confirmLabel: 'Sim, solicitar reagendamento',
            };
        default:
            return {
                tone: 'confirm',
                icon: <IconCheckCircle width="40" height="40" />,
                title: 'Você tem certeza?',
                description: '',
                confirmLabel: 'Sim, confirmar',
            };
    }
}

function StateCard({ variant, title, text, children, clinic, pill }) {
    return (
        <div className={`pac-state pac-state--${variant}`}>
            <div className="pac-state__icon" aria-hidden="true">
                {variant === 'success' && <IconCheckCircle width="30" height="30" />}
                {variant === 'info' && <IconCheckCircle width="30" height="30" />}
                {variant === 'warn' && <IconClock width="30" height="30" />}
                {variant === 'error' && <IconXCircle width="30" height="30" />}
            </div>
            <h2 className="pac-state__title">{title}</h2>
            {pill && (
                <span className={`pac-pill pac-pill--${pill.tone}`}>
                    {pill.icon}
                    {pill.label}
                </span>
            )}
            {text && <p className="pac-state__text">{text}</p>}
            {children}
            {clinic && <ContactClinicButton clinic={clinic} />}
        </div>
    );
}

/**
 * ContactClinicButton — botão que abre WhatsApp Web/App com a clínica.
 * Usa whatsapp da company_config quando disponível, senão o phone da clínica.
 */
function ContactClinicButton({ clinic }) {
    if (!clinic) return null;
    const raw = clinic.whatsapp || clinic.phone || '';
    const withCountry = normalizePhone(raw, clinic.countryCode || '');
    if (!withCountry) return null;
    const businessName = (clinic.name || '').trim();
    const greetingTarget = businessName ? ` ${businessName}` : '';
    const message = encodeURIComponent(
        `Olá${greetingTarget ? ',' : ''}${greetingTarget}! Estou entrando em contato pelo link de confirmação de agendamento.`
    );
    const url = `https://wa.me/${withCountry}?text=${message}`;

    return (
        <a
            className="pac-whatsapp-btn"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
        >
            <span className="pac-whatsapp-btn__icon" aria-hidden="true">
                <IconWhatsapp width="20" height="20" />
            </span>
            <span>Entrar em contato</span>
        </a>
    );
}

// ─── Text helpers ────────────────────────────────────────────────────────────

function buildUsedTitle(action) {
    switch (action) {
        case 'confirm':              return 'Presença já confirmada';
        case 'cancel':               return 'Cancelamento já registrado';
        case 'reschedule':           return 'Reagendamento já solicitado';
        case 'reschedule_completed': return 'Reagendamento realizado';
        case 'reschedule_done':      return 'Reagendamento realizado';
        default:                     return 'Resposta já registrada';
    }
}

function buildUsedVariant(action) {
    switch (action) {
        case 'confirm':              return 'success';
        case 'cancel':               return 'error';
        case 'reschedule':           return 'warn';
        case 'reschedule_completed': return 'success';
        case 'reschedule_done':      return 'success';
        default:                     return 'info';
    }
}

function buildUsedText(action) {
    switch (action) {
        case 'confirm':
            return 'Você já confirmou sua presença neste agendamento. Nos vemos no horário marcado! Se precisar alterar algo, fale conosco pelo WhatsApp.';
        case 'cancel':
            return 'Você já registrou o cancelamento deste agendamento por aqui. Se quiser remarcar, fale conosco pelo WhatsApp.';
        case 'reschedule':
            return 'Você já solicitou o reagendamento deste horário. Em breve entraremos em contato para combinar uma nova data — se preferir adiantar, fale conosco pelo WhatsApp.';
        case 'reschedule_completed':
            return 'Seu agendamento foi reagendado com sucesso! Confira os detalhes do novo horário abaixo. Se precisar alterar algo, fale conosco pelo WhatsApp.';
        case 'reschedule_done':
            return 'Seu agendamento foi reagendado com sucesso! Confira os detalhes do novo horário abaixo. Se precisar alterar algo, fale conosco pelo WhatsApp.';
        default:
            return 'Sua resposta já foi registrada. Se precisar alterar algo, entre em contato pelo WhatsApp.';
    }
}
// ─── WhatsApp helpers (para cancel/reschedule) ───────────────────────────────

/**
 * Monta a URL wa.me com uma mensagem pré-preenchida descrevendo a ação
 * (cancelar/reagendar) e o resumo do agendamento. Retorna string vazia
 * se a clínica não tiver telefone/WhatsApp cadastrado.
 */
function buildActionWhatsappUrl(action, appointment, clinic, shortDate, time) {
    if (!clinic) return '';
    const raw = clinic.whatsapp || clinic.phone || '';
    const withCountry = normalizePhone(raw, clinic.countryCode || '');
    if (!withCountry) return '';

    const businessName = clinic && clinic.name ? String(clinic.name).trim() : '';
    const professional = appointment && appointment.professional_name ? appointment.professional_name : '';
    const procedure    = appointment && appointment.procedure_name    ? appointment.procedure_name    : '';
    const patientName  = appointment && (appointment.patient_name || appointment.patient_first_name) ? (appointment.patient_name || appointment.patient_first_name) : '';

    const verb = action === 'cancel' ? 'cancelar' : 'reagendar';

    const lines = [];
    lines.push(`Olá, vou precisar ${verb} meu agendamento${businessName ? ` em ${businessName}` : ''}.`);
    lines.push('');
    if (patientName) lines.push(`Nome: ${patientName}`);
    if (shortDate)   lines.push(`Data: ${shortDate}`);
    if (time)        lines.push(`Horário: ${time}`);
    if (procedure)   lines.push(`Procedimento: ${procedure}`);
    if (professional) lines.push(`Profissional: ${professional}`);
    lines.push('');
    if (action === 'cancel') {
        lines.push('Não conseguirei comparecer.');
    } else {
        lines.push('Podemos combinar um novo horário?');
    }

    const message = encodeURIComponent(lines.join('\n'));
    return `https://wa.me/${withCountry}?text=${message}`;
}

/**
 * Abre o WhatsApp em nova aba. DEVE ser chamado DENTRO do handler de click
 * do usuário (mesma tick do gesto) para não ser bloqueado pelo popup blocker.
 */
function openWhatsappForAction(action, appointment, clinic, shortDate, time) {
    const url = buildActionWhatsappUrl(action, appointment, clinic, shortDate, time);
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
}

function buildSuccessPill(action) {
    switch (action) {
        case 'confirm':
            return { label: 'Confirmado', tone: 'success', icon: <IconCheckCircle width="14" height="14" /> };
        case 'cancel':
            return { label: 'Cancelado', tone: 'danger', icon: <IconXCircle width="14" height="14" /> };
        case 'reschedule':
            return { label: 'Reagendamento solicitado', tone: 'warn', icon: <IconRefresh width="14" height="14" /> };
        case 'reschedule_completed':
            return { label: 'Reagendado', tone: 'success', icon: <IconCheckCircle width="14" height="14" /> };
        case 'reschedule_done':
            return { label: 'Reagendado', tone: 'success', icon: <IconCheckCircle width="14" height="14" /> };
        default:
            return { label: 'Registrado', tone: 'success', icon: <IconCheckCircle width="14" height="14" /> };
    }
}

// ─── V3 — Reschedule WhatsApp helper (with selected slot) ────────────────────

/**
 * Builds a WhatsApp URL for reschedule with a specific selected slot.
 * Message follows the spec: asks the clinic to verify availability for the new time.
 */
function buildRescheduleWithSlotWhatsappUrl(appointment, clinic, shortDate, time, slot) {
    if (!clinic) return '';
    const raw = clinic.whatsapp || clinic.phone || '';
    const withCountry = normalizePhone(raw, clinic.countryCode || '');
    if (!withCountry) return '';

    const patientName  = appointment && (appointment.patient_name || appointment.patient_first_name) || '';
    const procedure    = appointment && appointment.procedure_name || '';
    const professional = appointment && appointment.professional_name || '';

    const selectedDateFormatted = slot ? formatShortDate(slot.date) : '';
    const selectedTime = slot ? slot.time : '';

    const lines = [];
    lines.push('Olá, gostaria de reagendar meu atendimento.');
    lines.push('');
    lines.push('Consulta original:');
    lines.push(`${shortDate} às ${time}`);
    lines.push('');
    lines.push('Gostaria de verificar a possibilidade de reagendar para:');
    lines.push(`${selectedDateFormatted} às ${selectedTime}`);
    lines.push('');
    if (patientName) lines.push(`Nome: ${patientName}`);
    if (procedure)   lines.push(`Procedimento: ${procedure}`);
    if (professional) lines.push(`Profissional: ${professional}`);

    const message = encodeURIComponent(lines.join('\n'));
    return `https://wa.me/${withCountry}?text=${message}`;
}

/**
 * Opens WhatsApp with selected slot message. Must be called within click handler.
 */
function openWhatsappRescheduleWithSlot(appointment, clinic, shortDate, time, slot) {
    const url = buildRescheduleWithSlotWhatsappUrl(appointment, clinic, shortDate, time, slot);
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
}

// ─── V4 — RescheduleSlotPicker component (uses same StepDate/StepTime) ───────

const toDateStrHelper = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const getMonthRangeHelper = (year, month) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const firstOfMonth = new Date(year, month, 1);
    const lastOfMonth = new Date(year, month + 1, 0);
    const start = (firstOfMonth < today) ? today : firstOfMonth;
    return { startDate: toDateStrHelper(start), endDate: toDateStrHelper(lastOfMonth) };
};

/**
 * RescheduleSlotPicker — uses the same StepDate + StepTime components
 * as the public schedule page, for a consistent calendar experience.
 */
function RescheduleSlotPicker({
    appointment, clinic, shortDate, time,
    selectedSlot, onSelectSlot,
    submitting, onBack, onConfirmSlot, onRescheduleSlot, onSkipSlot,
    postSubmitError,
}) {
    // Internal step: 'date' or 'time'
    const [internalStep, setInternalStep] = useState('date');
    const [selectedDate, setSelectedDate] = useState('');
    const [dateSlots, setDateSlots]       = useState([]);
    const [selectedTime, setSelectedTime] = useState('');

    // Range data (calendar availability)
    const [rangeData, setRangeData]       = useState({});
    const [rangeLoading, setRangeLoading] = useState(false);
    const [loadedMonths, setLoadedMonths] = useState({});

    const companySlug       = appointment?.company_slug || '';
    const professionalId    = appointment?.professional_id || '';
    const procedureDuration = appointment?.procedure_duration || 0;
    const availableDaysOfWeek = appointment?.available_days_of_week || [1, 2, 3, 4, 5];

    // Load month range
    const loadMonthRange = useCallback((year, month) => {
        if (!companySlug || !professionalId) return;

        const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`;
        if (loadedMonths[monthKey]) return;

        const { startDate, endDate } = getMonthRangeHelper(year, month);
        if (startDate > endDate) return;

        setRangeLoading(true);
        GetPublicAvailableSlotsRangeAPI(
            companySlug,
            professionalId,
            startDate,
            endDate,
            (res) => {
                setRangeLoading(false);
                if (res.success && res.dates) {
                    setRangeData(prev => ({ ...prev, ...res.dates }));
                    setLoadedMonths(prev => ({ ...prev, [monthKey]: true }));
                }
            },
            procedureDuration > 0 ? procedureDuration : undefined
        );
    }, [companySlug, professionalId, procedureDuration, loadedMonths]);

    // When a date is selected in the calendar, go to time step immediately
    const handleDateSelect = (dateStr) => {
        setSelectedDate(dateStr);
        setSelectedTime('');
        onSelectSlot(null);

        // Get slots from cache
        if (rangeData[dateStr]?.slots) {
            setDateSlots(rangeData[dateStr].slots);
        } else {
            setDateSlots([]);
        }
        setInternalStep('time');
    };

    const handleTimeSelect = (t) => {
        setSelectedTime(t);
        onSelectSlot({ date: selectedDate, time: t });
    };

    const handleBackToCalendar = () => {
        setInternalStep('date');
        onSelectSlot(null);
        setSelectedDate('');
        setSelectedTime('');
    };

    return (
        <div className="pac-slot-picker">
            {/* ── Step: Date ──────────────────────────────────────── */}
            {internalStep === 'date' && (
                <StepDate
                    selectedDate={selectedDate}
                    onSelect={handleDateSelect}
                    onNext={() => {}} // não precisa — o clique na data já avança
                    onBack={onBack}
                    availableDaysOfWeek={availableDaysOfWeek}
                    rangeData={rangeData}
                    rangeLoading={rangeLoading}
                    onMonthChange={loadMonthRange}
                />
            )}

            {/* ── Step: Time ──────────────────────────────────────── */}
            {internalStep === 'time' && (
                <StepTime
                    date={selectedDate}
                    slots={dateSlots}
                    slotsLoading={false}
                    selectedTime={selectedTime}
                    onSelect={handleTimeSelect}
                    onNext={() => selectedSlot && onConfirmSlot(selectedSlot)}
                    onBack={handleBackToCalendar}
                    renderActions={({ selectedTime: selTime, onBack: goBack }) => (
                        <div className="pac-slot-picker__actions">
                            <button className="ps-btn ps-btn--secondary" onClick={goBack}>
                                ← Voltar
                            </button>

                            {postSubmitError && (
                                <p className="pac-error-inline" style={{ margin: '8px 0' }}>{postSubmitError}</p>
                            )}

                            {selTime && selectedSlot && onRescheduleSlot && (
                                <button
                                    type="button"
                                    className="ps-btn ps-btn--primary pac-slot-picker__confirm-btn"
                                    disabled={submitting}
                                    onClick={() => onRescheduleSlot(selectedSlot)}
                                    style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                                >
                                    <IconRefresh width="18" height="18" />
                                    {submitting ? 'Reagendando…' : 'Reagendar'}
                                </button>
                            )}
                            {selTime && selectedSlot && (
                                <button
                                    type="button"
                                    className="ps-btn ps-btn--ghost pac-slot-picker__skip-btn"
                                    disabled={submitting}
                                    onClick={() => onConfirmSlot(selectedSlot)}
                                    style={{ width: '100%', marginTop: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                                >
                                    <IconWhatsapp width="16" height="16" />
                                    Pedir pelo WhatsApp
                                </button>
                            )}
                            <button
                                type="button"
                                className="ps-btn ps-btn--ghost pac-slot-picker__skip-btn"
                                onClick={onSkipSlot}
                                disabled={submitting}
                                style={{ width: '100%', marginTop: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                            >
                                <IconWhatsapp width="16" height="16" />
                                Prefiro combinar outro horário
                            </button>
                        </div>
                    )}
                />
            )}
        </div>
    );
}
